<template>
  <form class="flex flex-col space-y-4" @submit="onLogin">
    <div class="flex flex-col space-y-4">
      <InputField
        name="email"
        :label="t('email')"
        autocomplete="email"
        type="email"
        height="h-10"
      />

      <InputField
        name="password"
        :label="t('password')"
        type="password"
        autocomplete="current-password"
        height="h-10"
      />
      <StatusBanner v-if="loginError" variant="problem">
        {{ loginError }}
      </StatusBanner>
    </div>

    <CVButton variant="default" type="submit" class="w-full" :is-loading="isLoading">
      {{ t('login') }}
    </CVButton>
    <CVButton
      v-if="!Capacitor.isNativePlatform()"
      variant="default"
      type="button"
      class="w-full"
      @click="onLoginWithGoogle"
    >
      <GoogleIcon class="h-4 w-4" />
      {{ t('loginWithGoogle') }}
    </CVButton>
    <CVButton variant="link" type="button" @click="emit('onSwitchToPasswordForgot')">
      {{ t('forgotPassword') }}
    </CVButton>
  </form>
</template>

<script setup lang="ts">
import InputField from '@/components/InputField.vue';
import StatusBanner from '@/components/StatusBanner.vue';
import type { LoginDto } from '@/entities/auth/login-dto.entity';
import { useI18n } from 'vue-i18n';
import { loginSchema } from '@/validation/login.schema';
import { useForm } from 'vee-validate';
import { authService } from '@/api/auth.service';
import GoogleIcon from '@/assets/icons/google.svg?component';
import { Capacitor } from '@capacitor/core';

defineProps<{
  isLoading: boolean;
  loginError?: string;
}>();

const emit = defineEmits<{
  (e: 'onLogin', value: LoginDto): void;
  (e: 'onSwitchToPasswordForgot'): void;
}>();
const { t } = useI18n();

const { handleSubmit: handleLoginSubmit } = useForm<LoginDto>({
  validationSchema: loginSchema,
});

const onLogin = handleLoginSubmit(async (values) => {
  emit('onLogin', values);
});

const onLoginWithGoogle = () => {
  authService.startGoogleLogin();
};
</script>

<i18n lang="json">
{
  "en": {
    "email": "Email",
    "password": "Password",
    "forgotPassword": "Forgot Password?",
    "login": "Login",
    "loginWithGoogle": "Login with Google"
  },
  "de": {
    "email": "E-Mail",
    "password": "Passwort",
    "forgotPassword": "Passwort vergessen?",
    "login": "Anmelden",
    "loginWithGoogle": "Mit Google anmelden"
  },
  "es": {
    "email": "Correo electrónico",
    "password": "Contraseña",
    "forgotPassword": "¿Ha olvidado su contraseña?",
    "login": "Iniciar sesión",
    "loginWithGoogle": "Iniciar sesión con Google"
  }
}
</i18n>
